import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LocalStorageService } from "ngx-store";
import { BehaviorSubject, of, Subject } from "rxjs";
import { first, map, pluck, shareReplay, switchMap, tap } from "rxjs/operators";
import { RunAction } from "../model/dispatch.model";
import { Page } from "../model/pagination";
import { Url } from "../model/users.model";

@Injectable({
  providedIn: 'root'
})
export class RunActionService {
  private url: Url = this.storage.get('url');

  /**
   * Run action resource server endpoint.
   */
  public readonly endpoint = `${this.url.rest}/tms/run-actions`;

  /**
   * Snapshot of the list of all run actions, as a hot observable.
   */
  private readonly runActions = new BehaviorSubject<RunAction[]>([]);

  public constructor(
    private storage: LocalStorageService,
    private client : HttpClient
  ) { }

  /**
   * Get all run actions.
   * @returns Cold observable of run actions.
   */
  public getActions() {
    return this.runActions.pipe(
      switchMap((actions) => actions.length > 0
        ? of(actions)
        : this.fetchActions()
          .pipe(
            pluck('data'),
            tap((fetchedActions) => this.runActions.next(fetchedActions))
          )
      ),
      first()
    );
  }

  /**
   * Get the specific run action with the given id.
   * @param id Run action id.
   * @returns Run action object.
   */
  public getAction(id: RunAction['id']) {
    return this.runActions
      .pipe(
        map((actions) => actions.find((action) => action.id === id))
      );
  }

  /**
   * Fetch all run actions into a single page.
   * @returns Fetched run actions.
   */
  private fetchActions() {
    const params = { 'per_page': '-1' };

    return this.client.get<Page<RunAction>>(this.endpoint, { params });
  }
}
